import { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import type { Link } from '@super/shared-types'

const linkEditSchema = z.object({
  name: z.string().max(500).optional(),
  pronunciationType: z.enum(['phoneme', 'description']),
  pronunciationOrDescription: z.string(),
  sendToCallers: z.boolean(),
})

type LinkEditFormValues = z.infer<typeof linkEditSchema>

interface LinkEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  link: Link | null
  onSave?: () => void
}

export function LinkEditDialog({
  open,
  onOpenChange,
  link,
  onSave,
}: LinkEditDialogProps) {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const form = useForm<LinkEditFormValues>({
    resolver: zodResolver(linkEditSchema),
    defaultValues: {
      name: '',
      pronunciationType: 'phoneme' as const,
      pronunciationOrDescription: '',
      sendToCallers: true,
    },
  })

  // Reset form when dialog opens with new link
  useEffect(() => {
    if (open && link) {
      // Determine pronunciation type and value
      const hasPronunciation = link.pronunciation?.phoneme
      const hasAlias = link.alias

      form.reset({
        name: link.name || '',
        pronunciationType: hasAlias ? 'description' : 'phoneme',
        pronunciationOrDescription: hasAlias
          ? link.alias || ''
          : hasPronunciation || '',
        sendToCallers: link.sendToCallers ?? true,
      })
    }
  }, [open, link, form])

  const onSubmit: SubmitHandler<LinkEditFormValues> = async data => {
    if (!link?.id) {
      toast.error('Link ID is missing')
      return
    }

    if (!currentCompany?.id) {
      toast.error('Company ID is missing')
      return
    }

    // Map the form data based on pronunciation type
    const payload = {
      name: data.name,
      // If description type, send as alias; otherwise send as pronunciation with type
      ...(data.pronunciationType === 'description'
        ? {
            alias: data.pronunciationOrDescription || undefined,
            pronunciation: undefined, // Clear pronunciation if switching to description
            pronunciationType: undefined,
          }
        : {
            alias: undefined, // Clear alias if switching to pronunciation
            pronunciation: data.pronunciationOrDescription || undefined,
            pronunciationType: data.pronunciationType,
          }),
      sendToCallers: data.sendToCallers,
    }

    try {
      if (isSuperAdmin) {
        await knowledgeApiClient.updateLinkForCompany(
          link.id,
          currentCompany.id,
          payload,
        )
      } else {
        await knowledgeApiClient.updateLink(link.id, payload)
      }

      toast.success('Link updated successfully')
      onOpenChange(false)
      onSave?.()
    } catch (error) {
      console.error('Error updating link:', error)
      toast.error('Failed to update link')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit link details</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="name">Name</FormLabel>
                    <FormControl>
                      <Input
                        id="name"
                        placeholder="Enter a name for this link"
                        {...field}
                        className="mt-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <FormItem>
                <FormLabel htmlFor="link-display">Link</FormLabel>
                <div id="link-display" className="mt-2 p-3 bg-muted rounded-md">
                  <p className="text-xs font-mono break-all">{link?.url}</p>
                </div>
              </FormItem>
            </div>

            <FormField
              control={form.control}
              name="pronunciationType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pronunciation Guide</FormLabel>
                  <FormControl>
                    <Tabs
                      value={field.value}
                      onValueChange={value => {
                        field.onChange(value)
                        form.setValue('pronunciationOrDescription', '')
                      }}
                      className="mt-2"
                    >
                      <TabsList>
                        <TabsTrigger value="phoneme">Phoneme</TabsTrigger>
                        <TabsTrigger value="description">
                          Description / Alias
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pronunciationOrDescription"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder={
                        form.watch('pronunciationType') === 'phoneme'
                          ? 'Enter phonetic spelling (goo gull dot com slash maps)'
                          : "Enter description or alias (e.g., Google's main website)"
                      }
                      {...field}
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sendToCallers"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center space-x-2">
                    <FormControl>
                      <Switch
                        id="send-to-callers"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel htmlFor="send-to-callers" className="!mt-0">
                      Send link to callers
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Save pronunciation</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
